import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, MenuItem,
  Checkbox, FormControlLabel, Typography, Box, CircularProgress
} from "@mui/material";
import api from "../api/api";

// Expected backend fields:
// id_curso, numero_version, fecha_publicacion, descripcion_cambios, vigente (bool), vigencia_meses (int|null)
// To create: id_curso required. To edit: PATCH.

export default function VersionForm({ open, onClose, version, idCursoDefault, cursos, onSaved }) {
  const [idCurso, setIdCurso] = useState(idCursoDefault || "");
  const [numeroVersion, setNumeroVersion] = useState("");
  const [fechaPublicacion, setFechaPublicacion] = useState(""); // 'YYYY-MM-DD'
  const [descripcion, setDescripcion] = useState("");
  const [vigenciaMeses, setVigenciaMeses] = useState("");
  const [duracionHoras, setDuracionHoras] = useState("");
  const [instructor, setInstructor] = useState("");
  const [lugar, setLugar] = useState("");

  // Working sheets assigned to the course and selection for this version
  const [fichasCurso, setFichasCurso] = useState([]);
  const [fichasSeleccionadas, setFichasSeleccionadas] = useState(new Set());
  const [loadingFichas, setLoadingFichas] = useState(false);

  useEffect(() => {
    if (version) {
      setIdCurso(version.id_curso);
      setNumeroVersion(version.numero_version);
      setFechaPublicacion(version.fecha_publicacion ?? "");
      setDescripcion(version.descripcion_cambios ?? "");
      setVigenciaMeses(version.vigencia_meses ?? "");
      setDuracionHoras(version.duracion_horas ?? "");
      setInstructor(version.instructor ?? "");
      setLugar(version.lugar ?? "");
    } else {
      setIdCurso(idCursoDefault || "");
      setNumeroVersion("");
      setFechaPublicacion(new Date().toISOString().slice(0,10));
      setDescripcion("");
      setVigenciaMeses("");
      setDuracionHoras("");
      setInstructor("");
      setLugar("");
    }
    setFichasSeleccionadas(new Set());
  }, [version, idCursoDefault]);

  // Load the course's working sheets when idCurso changes
  useEffect(() => {
    if (!idCurso) { setFichasCurso([]); return; }
    setLoadingFichas(true);
    api.get(`/fichas/cursos/${idCurso}`)
      .then(r => setFichasCurso(r.data))
      .catch(() => setFichasCurso([]))
      .finally(() => setLoadingFichas(false));
  }, [idCurso]);

  // Load working sheets already assigned to the version being edited
  useEffect(() => {
    if (!version) { setFichasSeleccionadas(new Set()); return; }
    api.get(`/versiones/${version.id_version}/fichas`)
      .then(r => setFichasSeleccionadas(new Set(r.data.map(f => f.codigo))))
      .catch(() => setFichasSeleccionadas(new Set()));
  }, [version]);

  const toggleFicha = (codigo) => {
    setFichasSeleccionadas(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const guardar = async () => {
    // Simple validation
    if (!idCurso) return alert("Select the course.");
    if (!numeroVersion.trim()) return alert("Enter the version number.");
    if (!fechaPublicacion) return alert("Enter the publication date.");

    const payload = {
      id_curso: parseInt(idCurso),
      numero_version: numeroVersion.trim(),
      fecha_publicacion: fechaPublicacion,
      descripcion_cambios: descripcion.trim() || null,
      // 'vigente' is handled by the /publicar endpoint; not sent here.
      vigencia_meses: vigenciaMeses === "" ? null : parseInt(vigenciaMeses),
      duracion_horas: duracionHoras === "" ? null : parseFloat(duracionHoras),
      instructor: instructor.trim() || null,
      lugar: lugar.trim() || null,
    };

    try {
      let versionId;
      if (version) {
        // EDIT
        const patch = {
          numero_version: payload.numero_version,
          fecha_publicacion: payload.fecha_publicacion,
          descripcion_cambios: payload.descripcion_cambios,
          vigencia_meses: payload.vigencia_meses,
          duracion_horas: payload.duracion_horas,
          instructor: payload.instructor,
          lugar: payload.lugar,
        };
        await api.patch(`/versiones/${version.id_version}`, patch);
        versionId = version.id_version;
      } else {
        // CREATE
        const res = await api.post("/versiones", payload);
        versionId = res.data.id_version;
      }
      // Save working sheets if the course has working sheets assigned
      if (fichasCurso.length > 0) {
        await api.put(`/versiones/${versionId}/fichas`, [...fichasSeleccionadas]);
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      console.error(e);
      alert("Error saving the version. Check permissions and data.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{version ? "Edit version" : "New version"}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              select fullWidth label="Course"
              value={idCurso}
              onChange={(e) => setIdCurso(e.target.value)}
              disabled={!!version} // cannot change course when editing
            >
              {cursos.map(c => (
                <MenuItem key={c.id_curso} value={c.id_curso}>
                  {c.nombre_curso} — ({c.tipo})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Version number"
              fullWidth
              value={numeroVersion}
              onChange={(e) => setNumeroVersion(e.target.value)}
              placeholder="1.0, 2026.01, etc."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Publication date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={fechaPublicacion}
              onChange={(e) => setFechaPublicacion(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Change description"
              fullWidth
              multiline rows={3}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Validity (months)"
              fullWidth
              type="number" inputProps={{ min: 1, max: 120 }}
              value={vigenciaMeses}
              onChange={(e) => setVigenciaMeses(e.target.value)}
              placeholder="E.g. 24 (leave empty if not applicable)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Course duration (hours)"
              fullWidth
              type="number" inputProps={{ min: 0.5, max: 999, step: 0.5 }}
              value={duracionHoras}
              onChange={(e) => setDuracionHoras(e.target.value)}
              placeholder="E.g. 8 (leave empty if not applicable)"
              helperText="Used as the default value when logging training sessions"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Instructor"
              fullWidth
              value={instructor}
              onChange={(e) => setInstructor(e.target.value)}
              placeholder="Instructor's name"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Location / Room"
              fullWidth
              value={lugar}
              onChange={(e) => setLugar(e.target.value)}
              placeholder="E.g. Training room A"
            />
          </Grid>

          {/* Working sheets changed in this version */}
          {(loadingFichas || fichasCurso.length > 0) && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 0.5 }}>
                Working sheets changed in this version
              </Typography>
              {loadingFichas ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading working sheets…</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    maxHeight: 190,
                    overflowY: "auto",
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {fichasCurso.map(f => (
                    <FormControlLabel
                      key={f.codigo}
                      control={
                        <Checkbox
                          size="small"
                          checked={fichasSeleccionadas.has(f.codigo)}
                          onChange={() => toggleFicha(f.codigo)}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <Typography variant="body2" component="span" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                            {f.codigo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {f.nombre}
                          </Typography>
                        </Box>
                      }
                      sx={{ display: "flex", alignItems: "flex-start", ml: 0, mb: 0.25 }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Check the working sheets that changed in this version of the course
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={guardar}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}